/* eslint-disable react-hooks/exhaustive-deps */
import YAML from "yaml";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { tokens } from "../../../theme";
import Header from "../../../components/Header";
import DataGridButton from "../../../components/DataGridButton";
import { Box, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { useListAllControlPlanes } from "../../../api/tessellGcpDeployments";
import { dataGridButtonTypography } from "../../../styles/DataGridStyles";
import {
  dataGridButtonStyles,
  envListButtonStyles,
  iamDividerStyles,
} from "../../../styles/buttonStyles";
import { tabbedDataGridPage } from "../../../styles/commonPageStyles";
import { Info, Sync } from "@mui/icons-material";
import { useUpgradeDialog } from "./UpgradeDialog";
import { useNotification } from "../../../components/Notification";
import useIamModal from "../../IamModal/UseIamModal";
import { useHasPermission } from "../../../api/iam";
import DataTable from "../../../components/DataTable";
import StatusChip from "../../../components/StatusChip";
import TimeCell from "../../../components/TimeCell";

const styles = {
  RmoTypography: { whiteSpace: "pre-wrap", fontFamily: "monospace" },
  // Column layout so the grid takes every pixel left under the header row.
  mainBox: {
    ...tabbedDataGridPage.tabContentMainBox,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  headerGrid: { ...tabbedDataGridPage.gridContainer, flexShrink: 0 },
  dataTable: { flex: 1, minHeight: 0 },
};

const UpgradeCell = ({ deploymentStage, name, setOpenUpgradeDialog }) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const canUpgrade = useHasPermission(
    `/apps/tessellgcp/deployments/${deploymentStage}/control-planes/${name}`,
    "PUT",
    "UPGRADE"
  );
  return (
    <DataGridButton
      sx={{
        ...dataGridButtonStyles.wakeUpButton,
        ...envListButtonStyles(colors).rowActionState,
      }}
      onClick={() => setOpenUpgradeDialog(name)}
      disabled={!canUpgrade}
    >
      UPGRADE
    </DataGridButton>
  );
};

const ControlPlanes = () => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  const { deploymentStage } = useParams();

  const [controlPlanes, setControlPlanes] = useState([]);

  const {
    postData: listAllControlPlanes,
    response: listAllControlPlanesResp,
    isLoading: isLoadingControlPlanes,
  } = useListAllControlPlanes(deploymentStage);

  useEffect(() => {
    listAllControlPlanes();
  }, [deploymentStage]);

  const { IamButton, IamModal, canViewIamModal } = useIamModal(
    `/apps/tessellgcp/deployments/${deploymentStage}/control-planes`
  );
  const { notificationComponent, displayNotification } = useNotification();
  const { upgradeDialog, setOpenUpgradeDialog } = useUpgradeDialog(
    deploymentStage,
    listAllControlPlanes,
    displayNotification
  );

  useEffect(() => {
    if (Array.isArray(listAllControlPlanesResp?.controlPlanes)) {
      setControlPlanes(listAllControlPlanesResp.controlPlanes);
    } else {
      setControlPlanes([]);
    }
  }, [listAllControlPlanesResp]);

  const columns = [
    {
      field: "name",
      renderHeader: () => "Name",
      flex: 1,
      cellClassName: "name-column--cell",
      renderCell: ({ row: { name } }) => name || "",
    },
    {
      field: "releaseLabel",
      flex: 1,
      renderHeader: () => "Release Label",
      renderCell: ({ row: { releaseLabel, releaseManifestOverride } }) => (
        <>
          {releaseLabel ?? ""}
          {releaseManifestOverride ? (
            <Tooltip
              title={
                <Typography variant="body2" sx={styles.RmoTypography}>
                  {YAML.stringify({ RMO: releaseManifestOverride }, null, 2)}
                </Typography>
              }
            >
              <Info fontSize="small" />
            </Tooltip>
          ) : (
            <></>
          )}
        </>
      ),
    },
    {
      field: "projectId",
      flex: 1,
      renderHeader: () => "Project Id",
      renderCell: ({ row: { projectId } }) => projectId ?? "",
    },
    {
      field: "region",
      flex: 1,
      renderHeader: () => "Region",
      renderCell: ({ row: { region } }) => region ?? "",
    },
    {
      field: "deploymentStatus",
      flex: 1,
      renderHeader: () => "Status",
      renderCell: ({ row: { deploymentStatus } }) => (
        <StatusChip value={deploymentStatus} />
      ),
    },
    {
      field: "dateModified",
      flex: 1.3,
      minWidth: 118,
      renderHeader: () => "Date Modified",
      renderCell: ({ row: { dateModified } }) => (
        <TimeCell value={dateModified} />
      ),
    },
    {
      width: 140,
      minWidth: 140,
      renderHeader: () => "Upgrade",
      headerAlign: "center",
      align: "center",
      renderCell: ({ row: { name } }) => (
        <UpgradeCell
          deploymentStage={deploymentStage}
          name={name}
          setOpenUpgradeDialog={setOpenUpgradeDialog}
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      flex: 0.5,
      getActions: ({ row }) =>
        [
          <GridActionsCellItem
            label="Delete"
            onClick={() => {}}
            disabled
            showInMenu
          />,
        ].filter(Boolean),
    },
  ];

  return (
    <Box sx={styles.mainBox}>
      {upgradeDialog}
      {notificationComponent}
      {IamModal}
      <Grid sx={styles.headerGrid} container columns={2}>
        <Grid xs={1}>
          <Header
            title="TessellGCP Control Planes"
            subtitle={`See the list ${deploymentStage} control planes`}
          />
        </Grid>
        <Grid xs={1}>
          <Box sx={{ ...tabbedDataGridPage.topButtonStack, gap: "8px" }}>
            {IamButton}
            {canViewIamModal ? <Box sx={iamDividerStyles(colors)} /> : null}
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={() => listAllControlPlanes()}
            >
              <Sync sx={{ fontSize: 16, color: "#fff" }} />
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                RELOAD
              </Typography>
            </DataGridButton>
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={() => {}}
              disabled
            >
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                CREATE NEW
              </Typography>
            </DataGridButton>
          </Box>
        </Grid>
      </Grid>
      {/* Keyed on the stage for the same reason as the Tessell control planes
          page: the router keeps this mounted when only :deploymentStage
          changes, so without a key the grid carries its filters between
          stages. */}
      <DataTable
        key={deploymentStage}
        hideFooter
        height="100%"
        sx={styles.dataTable}
        getRowId={(row) => row?.name}
        loading={isLoadingControlPlanes}
        rows={controlPlanes}
        columns={columns}
      />
    </Box>
  );
};

export default ControlPlanes;
